<!--
  The viewers strip. TASK-041 Phase 2.

  Who is on this document right now: circular avatars with initials, 36px,
  overlapping by 8px, five at most and then `+n`. An agent is told apart by a
  ring in the one accent rather than by a different shape -- an agent is a
  participant, not a different class of thing -- and its tooltip names the
  person it acts for ("Claude, for Euan Jardine").

  An agent is in this list EXACTLY while it has a `wait` parked on the
  document, so a comment now reaches it within seconds. That is the one fact
  the reader came here for, so it is also said in words beside the avatars
  ("Claude is watching"), readable without hovering, and announced politely
  when it changes. The words live in a status region that is always in the
  DOM, so a screen reader hears the change rather than a region appearing.

  It draws what it is given. The page owns the fetch, the `presence` event and
  the heartbeat (DocView); the order and the overflow are lib/presence.ts.
-->
<script lang="ts">
  import { stripView, viewerLabel, watchingCue } from '../presence'
  import type { Viewer } from '../types'

  let {
    viewers,
    compact = false,
  }: {
    viewers: Viewer[]
    /** The narrow-screen row under the bar, rather than the bar itself. */
    compact?: boolean
  } = $props()

  const view = $derived(stripView(viewers))
  const cue = $derived(watchingCue(viewers))
</script>

<div class="viewers" class:compact>
  <p class="watching meta-label" class:on={cue !== ''} role="status" aria-live="polite">{cue}</p>
  {#if view.shown.length}
    <ul class="strip" aria-label="On this document">
      {#each view.shown as v (v.viewerId)}
        <li class="avatar" class:agent={v.kind === 'agent'} class:you={v.you} title={viewerLabel(v)}>
          <span aria-hidden="true">{v.initials}</span>
          <span class="visually-hidden">{viewerLabel(v)}{v.kind === 'agent' ? ', an agent, watching' : ''}</span>
        </li>
      {/each}
      {#if view.more}
        <li class="avatar more" title={`${view.more} more`}>
          <span aria-hidden="true">+{view.more}</span>
          <span class="visually-hidden">and {view.more} more</span>
        </li>
      {/if}
    </ul>
  {/if}
</div>

<style>
  .viewers {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 0;
  }

  .compact {
    justify-content: flex-end;
  }

  /* The words. Empty, it takes no room -- but stays in the accessibility
     tree, so the region is there to be announced when words arrive. */
  .watching {
    margin: 0;
    color: var(--color-red-deep);
    white-space: nowrap;
  }

  .watching:not(.on) {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }

  /* A small red dot before the words, the one accent, like a recording light. */
  .watching.on::before {
    content: '';
    display: inline-block;
    width: 0.4375rem;
    height: 0.4375rem;
    margin-right: 0.5rem;
    border-radius: 999px;
    background: var(--color-red);
    vertical-align: 0.08em;
  }

  .strip {
    display: flex;
    align-items: center;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .avatar {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 36px;
    height: 36px;
    box-sizing: border-box;
    border-radius: 999px;
    /* The paper edge is what keeps overlapping circles legible as circles. */
    border: 2px solid var(--paper);
    background: var(--color-plinth);
    color: var(--ink);
    font-family: var(--font-sans);
    font-size: 0.8125rem;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: 1;
    cursor: default;
  }

  .avatar + .avatar {
    margin-left: -8px;
  }

  /* An agent: the same circle, ringed in the accent. */
  .avatar.agent {
    box-shadow: 0 0 0 2px var(--color-red);
    color: var(--color-red-deep);
    z-index: 1;
  }

  .avatar.more {
    background: var(--paper);
    color: var(--color-ink-soft);
    border-color: var(--color-line);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
    margin: 0;
  }
</style>
